import { useEffect, useState } from "react";
import { Wrench } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { EchoGame } from "@/features/maintenance/EchoGame";
import { refreshMaintenanceStatus } from "@/features/maintenance/maintenanceApi";
import {
  isClosed,
  maintenanceMessage,
  setMaintenance,
  useMaintenance,
} from "@/features/maintenance/maintenanceStore";
import { useAuth } from "@/features/auth/AuthContext";
import type { Locale } from "@/api/types";
import { LOCALE_STORAGE_KEY, useI18n } from "@/i18n/I18nContext";
import { safeRedirectPath } from "@/lib/errors";
import { BrandLockup } from "@/shared/ui/brand";
import { Button } from "@/shared/ui/button";

const LOCALES: ReadonlyArray<{ value: Locale; label: string }> = [
  { value: "en", label: "EN" },
  { value: "id", label: "ID" },
];

/**
 * Where everyone lands while maintenance closes the app. It makes no call that
 * needs a session, so it cannot end one, and it says plainly whether the visitor
 * is still signed in. When maintenance ends it sends them back to the page they
 * came from. The page fits one screen on desktop and phones, with a pattern game
 * (Echo) to play while waiting.
 */
export function MaintenancePage() {
  const status = useMaintenance();
  const location = useLocation();
  const { status: authStatus } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const [checking, setChecking] = useState(false);

  // Indonesian is the default here. A language already chosen on this device, or saved
  // on the account, wins. This never writes to the account.
  useEffect(() => {
    let chosen: boolean;
    try {
      chosen = window.localStorage.getItem(LOCALE_STORAGE_KEY) !== null;
    } catch {
      // Storage unavailable: fall back to Indonesian too.
      chosen = false;
    }
    if (!chosen) void setLocale("id", { persist: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const previous = document.title;
    document.title = `${t("maintenance.title")} · Memoru`;
    return () => {
      document.title = previous;
    };
  }, [t]);

  if (!isClosed(status)) {
    const from = typeof location.state === "object" && location.state !== null && "from" in location.state
      ? String((location.state as { from: unknown }).from)
      : "/";
    return <Navigate to={safeRedirectPath(from)} replace />;
  }

  const message = maintenanceMessage(status, locale) || t("maintenance.description");
  const check = async () => {
    setChecking(true);
    const next = await refreshMaintenanceStatus();
    if (next) setMaintenance(next);
    setChecking(false);
  };

  return (
    // Sizing the game so the page does not scroll:
    // - Phones: the game area below takes whatever height is left and the board
    //   fits it (container query units, see EchoGame), so it adapts to the
    //   language, a long admin message and the browser's toolbars.
    // - Desktop: the board is sized from the viewport height minus --game-rest.
    // The board has a minimum size, so on a very short window (a phone on its
    // side) the page scrolls instead of clipping.
    <div className="flex min-h-dvh flex-col bg-canvas text-foreground [--game-rest:17rem]">
      <header className="flex items-center justify-between gap-4 px-4 py-2 sm:px-8 min-[900px]:py-3">
        <BrandLockup markSize={28} wordmarkClassName="text-xl" />
        <div role="group" aria-label={t("maintenance.language")} className="inline-flex rounded-full bg-secondary p-1">
          {LOCALES.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={locale === option.value}
              onClick={() => void setLocale(option.value, { persist: false })}
              className="h-9 min-w-11 rounded-full px-3 text-sm font-medium text-muted-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-pressed:bg-card aria-pressed:text-foreground aria-pressed:shadow-[0_0_0_1px_var(--border)]"
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col items-center gap-2 px-4 pb-4 sm:px-8 min-[900px]:grid min-[900px]:grid-cols-[minmax(0,1fr)_auto] min-[900px]:content-center min-[900px]:items-stretch min-[900px]:gap-x-16 min-[900px]:gap-y-6">
        <section className="flex max-w-xl flex-col items-center gap-2 text-center min-[900px]:col-start-1 min-[900px]:row-start-1 min-[900px]:items-start min-[900px]:gap-3 min-[900px]:self-end min-[900px]:text-left">
          <span className="inline-flex h-7 items-center gap-2 rounded-full bg-warning px-3 text-xs font-medium text-warning-foreground max-[899px]:hidden">
            <Wrench aria-hidden="true" className="size-4" />
            {t("maintenance.tag")}
          </span>
          <h1 className="font-heading text-[length:clamp(1.5rem,min(5vw,7dvh),3rem)] font-semibold leading-tight tracking-tight">
            {t("maintenance.title")}
          </h1>
          <p role="status" className="text-sm text-muted-foreground min-[900px]:text-base">{message}</p>
          <p className="text-base text-muted-foreground max-[899px]:hidden">{t("maintenance.game.invite")}</p>
        </section>

        {/* Phones: this area is what is left of the screen, and the game fits it. The size
            container is an absolutely positioned child because container query units
            inside a flex item that grew to fill the space resolve to 0 (checked in
            Chrome); a box sized by inset: 0 gets its real size.
            Desktop: both boxes disappear (display: contents) and the game is a grid cell. */}
        <div className="relative min-h-[19rem] w-full flex-1 min-[900px]:contents">
          <div className="absolute inset-0 flex items-center justify-center [container-type:size] min-[900px]:contents">
            <EchoGame className="min-[900px]:col-start-2 min-[900px]:row-span-2 min-[900px]:row-start-1 min-[900px]:self-center" />
          </div>
        </div>

        <div className="flex max-w-xl flex-col items-center gap-2 text-center min-[900px]:col-start-1 min-[900px]:row-start-2 min-[900px]:items-start min-[900px]:self-start min-[900px]:text-left">
          {authStatus === "authenticated" ? (
            <p className="text-sm text-muted-foreground">{t("maintenance.stillSignedIn")}</p>
          ) : null}
          <Button type="button" variant="outline" size="lg" onClick={() => void check()} disabled={checking}>
            {checking ? t("maintenance.checking") : t("maintenance.retry")}
          </Button>
          <p className="text-xs text-muted-foreground max-[899px]:hidden">{t("maintenance.autoCheck")}</p>
        </div>
      </main>
    </div>
  );
}
